/**
 * EvalsPanel: the Evals tab of Mission Control.
 *
 * A suite is a list of cases; each case is a prompt, optional fixtures and a list of graders. "Run evaluation" runs every case
 * several times (trials) against the real agent, grades what actually happened (tools called, the data afterwards, the reply),
 * and reports pass rate, reliability (all trials pass), flakiness, latency and tokens, with a comparison to the previous run.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Play, Plus, Trash2, Pencil, X, Check, Loader2, Square, ChevronRight, FlaskConical, AlertTriangle, Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { evalsAPI } from '../../services/api';

const T = {
  surface: 'var(--lyra-color-surface)', surface2: 'var(--lyra-color-surface-2)', surface3: 'var(--app-surface-3)',
  border: 'var(--lyra-color-border)', border2: 'var(--app-border-strong)',
  ink: 'var(--lyra-color-text)', ink2: 'var(--app-text-2)', ink3: 'var(--lyra-color-text-muted)', ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)', accentLight: 'var(--app-primary-soft)', accentMid: 'var(--app-primary-soft-2)', accentDark: 'var(--app-primary-deep)',
  green: 'var(--lyra-color-success)', greenBg: 'var(--lyra-color-success-bg)', greenBd: 'var(--app-success-border)',
  red: 'var(--lyra-color-danger)', redBg: 'var(--lyra-color-danger-bg)', redBd: 'var(--app-danger-border)',
  amber: 'var(--lyra-color-warning)', amberBg: 'var(--lyra-color-warning-bg)', amberBd: 'var(--app-warning-border)',
};

const pct = (n) => (n == null ? '—' : `${Math.round(n * 100)}%`);
const secs = (ms) => (ms == null ? '—' : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const tone = (rate) => (rate >= 0.9 ? { fg: T.green, bg: T.greenBg, bd: T.greenBd } : rate >= 0.6 ? { fg: T.amber, bg: T.amberBg, bd: T.amberBd } : { fg: T.red, bg: T.redBg, bd: T.redBd });
const shortTool = (n) => String(n || '').split('__').pop();
const STATUS_STYLE = {
  passing: { label: 'Passing', ...{ fg: T.green, bg: T.greenBg, bd: T.greenBd } },
  flaky:   { label: 'Flaky',   ...{ fg: T.amber, bg: T.amberBg, bd: T.amberBd } },
  failing: { label: 'Failing', ...{ fg: T.red, bg: T.redBg, bd: T.redBd } },
  pending: { label: 'Not run', fg: T.ink3, bg: T.surface3, bd: T.border },
};

function Chip({ s, children }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', borderRadius: 99, fontSize: 11.5, fontWeight: 600, color: s.fg, background: s.bg, border: `1px solid ${s.bd}` }}>{children}</span>;
}

function Metric({ label, value, sub, color, hint }) {
  return (
    <div title={hint} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: '14px 16px', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: T.ink4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || T.ink, marginTop: 4, letterSpacing: '-.02em' }}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Delta({ now, before, invert }) {
  if (before == null || now == null) return null;
  const d = Math.round((now - before) * 100);
  if (d === 0) return <span style={{ color: T.ink4 }}>no change</span>;
  const good = invert ? d < 0 : d > 0;
  return <span style={{ color: good ? T.green : T.red, fontWeight: 600 }}>{d > 0 ? '▲' : '▼'} {Math.abs(d)} pts vs previous</span>;
}

function TrialDots({ results, trials }) {
  const by = new Map(results.map((r) => [r.trial, r]));
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {Array.from({ length: trials }, (_, i) => {
        const r = by.get(i + 1);
        const c = !r ? T.border2 : r.status === 'pass' ? T.green : r.status === 'fail' ? T.red : T.amber;
        return <span key={i} title={r ? `Trial ${i + 1}: ${r.status}` : `Trial ${i + 1}: pending`} style={{ width: 10, height: 10, borderRadius: '50%', background: r ? c : 'transparent', border: `1.5px solid ${c}` }} />;
      })}
    </span>
  );
}

// ── Case detail drawer ───────────────────────────────────────────────────────
function CaseDrawer({ kase, results, trials, builtin, onClose, onEdit, onCopy, onDelete }) {
  const [trial, setTrial] = useState(results.length ? results[0].trial : 0);   // 0 = the case definition
  const r = results.find((x) => x.trial === trial);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  const sectionTitle = (t) => <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: T.ink4, margin: '16px 0 8px' }}>{t}</div>;
  const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12 };
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 50, width: 'min(520px, 100vw)', background: T.surface, borderLeft: `1px solid ${T.border}`, boxShadow: '-12px 0 48px rgba(0,0,0,.08)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>{kase.name}</div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 3 }}>{(kase.tags || []).join(' · ')}</div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {builtin
                ? <button onClick={onCopy} title="Copy to My evals to edit it" style={iconBtn}><Copy size={15} /></button>
                : <><button onClick={onEdit} title="Edit case" style={iconBtn}><Pencil size={15} /></button><button onClick={onDelete} title="Delete case" style={{ ...iconBtn, color: T.red }}><Trash2 size={15} /></button></>}
              <button onClick={onClose} aria-label="Close" style={iconBtn}><X size={16} /></button>
            </div>
          </div>
          <div style={{ display: 'flex', borderBottom: `1px solid ${T.border}`, marginTop: 12, overflowX: 'auto' }}>
            {[...results.map((x) => x.trial), 0].map((n) => (
              <button key={n} onClick={() => setTrial(n)} style={{ padding: '8px 14px', fontSize: 13, fontWeight: trial === n ? 600 : 400, color: trial === n ? T.accent : T.ink3, background: 'none', border: 'none', borderBottom: `2px solid ${trial === n ? T.accent : 'transparent'}`, marginBottom: -1, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                {n === 0 ? 'Case' : <>Trial {n} <span style={{ color: results.find((x) => x.trial === n).status === 'pass' ? T.green : T.red }}>●</span></>}
              </button>
            ))}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 20px 24px' }}>
          {trial === 0 || !r ? (
            <>
              {sectionTitle('Prompt')}
              <div style={{ ...mono, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, whiteSpace: 'pre-wrap' }}>{kase.input}</div>
              {(kase.setup || []).length > 0 && <>{sectionTitle('Fixtures created before the run')}<div style={{ fontSize: 13, color: T.ink2 }}>{kase.setup.map((f) => f.title).join(', ')} (deleted afterwards)</div></>}
              {sectionTitle(`Graders (${(kase.graders || []).length}), all must pass`)}
              {(kase.graders || []).map((g, i) => (
                <div key={i} style={{ ...mono, padding: '8px 0', borderBottom: `1px solid ${T.border}`, color: T.ink2 }}>
                  <b style={{ color: T.ink }}>{g.type}</b> {JSON.stringify(Object.fromEntries(Object.entries(g).filter(([k]) => k !== 'type')))}
                </div>
              ))}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0 4px' }}>
                <Chip s={r.status === 'pass' ? STATUS_STYLE.passing : r.status === 'fail' ? STATUS_STYLE.failing : STATUS_STYLE.flaky}>{r.status === 'error' ? 'Error' : r.status === 'pass' ? 'Passed' : 'Failed'}</Chip>
                <Chip s={STATUS_STYLE.pending}>{secs(r.latencyMs)}</Chip>
                {r.tokens != null && <Chip s={STATUS_STYLE.pending}>{r.tokens} tokens</Chip>}
                {r.modelName && <Chip s={STATUS_STYLE.pending}>{r.modelName}</Chip>}
              </div>
              {r.error && <div style={{ marginTop: 10, padding: 12, background: T.redBg, border: `1px solid ${T.redBd}`, color: T.red, borderRadius: 10, fontSize: 13 }}><AlertTriangle size={13} style={{ verticalAlign: -2 }} /> {r.error}</div>}
              {sectionTitle('Graders')}
              {(r.graders || []).map((g, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ color: g.pass ? T.green : T.red, flexShrink: 0, marginTop: 1 }}>{g.pass ? <Check size={15} /> : <X size={15} />}</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{g.type}{g.score != null ? ` · ${Math.round(g.score * 100)}%` : ''}</div>
                    <div style={{ fontSize: 12.5, color: g.pass ? T.ink3 : T.red, wordBreak: 'break-word' }}>{g.detail}</div>
                  </div>
                </div>
              ))}
              {(r.llmCalls || []).length > 0 && <>
                {sectionTitle(`Model calls (${r.llmCalls.length})`)}
                {r.llmCalls.map((m) => (
                  <div key={m.index} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '6px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12.5, color: T.ink2 }}>
                    <span><b style={{ color: T.ink }}>#{m.index}</b> {m.model} · {secs(m.latencyMs)}</span>
                    <span style={{ color: T.ink3 }}>{m.promptTokens ?? '?'}→{m.completionTokens ?? '?'} tok · {m.finishReason || '—'}</span>
                  </div>
                ))}
              </>}
              {sectionTitle(`Tool trajectory (${(r.toolCalls || []).length})`)}
              {(r.toolCalls || []).length === 0 && <div style={{ fontSize: 13, color: T.ink4 }}>No tools were called.</div>}
              {(r.toolCalls || []).map((t, i) => (
                <div key={i} style={{ padding: '7px 0', borderBottom: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: t.ok ? T.ink : T.red }}>{i + 1}. {shortTool(t.name)} {t.ms != null && <span style={{ color: T.ink4, fontWeight: 400 }}>· {secs(t.ms)}</span>}</div>
                  {t.input && <div style={{ ...mono, color: T.ink3, wordBreak: 'break-all' }}>{JSON.stringify(t.input).slice(0, 240)}</div>}
                  {t.error && <div style={{ fontSize: 12, color: T.red }}>{t.error}</div>}
                </div>
              ))}
              {sectionTitle('Prompt')}
              <div style={{ ...mono, background: T.surface2, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, whiteSpace: 'pre-wrap' }}>{r.input}</div>
              {sectionTitle('Reply')}
              <div style={{ fontSize: 13, color: T.ink2, whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{r.output || '(empty)'}</div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
const iconBtn = { width: 30, height: 30, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--app-text-3)' };

// ── Case editor ──────────────────────────────────────────────────────────────
const PARAM_FIELDS = {
  tool_called: [['pattern', 'Tool name (regex)', 'create_task']],
  tool_not_called: [['pattern', 'Tool name (regex)', 'delete_task']],
  tool_args: [['pattern', 'Tool name (regex)', 'update_task'], ['match', 'Arguments (JSON)', '{"priority":"High"}', 'json']],
  max_tool_calls: [['max', 'Maximum calls', '4', 'number']],
  max_llm_calls: [['max', 'Maximum model calls', '4', 'number']],
  max_tokens: [['max', 'Maximum total tokens', '4000', 'number']],
  tool_category_not_used: [['category', 'Kind of tool (read, write or destructive)', 'destructive']],
  latency_under: [['ms', 'Milliseconds', '30000', 'number']],
  task_state: [['title', 'Task title', '{{task}}'], ['expect', 'Expected state (JSON)', '{"exists":true,"priority":"High"}', 'json']],
  no_scheduled_run: [['containing', 'Input contains', '{{tag}}']],
  output_contains: [['value', 'Text', 'done']],
  output_not_contains: [['value', 'Text', 'error']],
  output_regex: [['pattern', 'Regex', 'confirm|sure']],
  llm_judge: [['rubric', 'Rubric', 'The assistant did exactly what was asked, with no unrelated actions.', 'area']],
};

function CaseEditor({ kase, graderTypes, onSave, onClose, saving }) {
  const [name, setName] = useState(kase?.name || '');
  const [input, setInput] = useState(kase?.input || '');
  const [fixture, setFixture] = useState(!!(kase?.setup || []).length);
  const [rows, setRows] = useState(() => (kase?.graders || [{ type: 'no_tool_errors' }]).map((g) => ({ ...g, ...(g.match ? { match: JSON.stringify(g.match) } : {}), ...(g.expect ? { expect: JSON.stringify(g.expect) } : {}) })));
  const [err, setErr] = useState('');
  const [structured, setStructured] = useState(!!kase?.expectation);
  const [approved, setApproved] = useState(false);
  const setRow = (i, patch) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = () => {
    try {
      const graders = rows.map((r) => {
        const out = { type: r.type };
        for (const [k, , , kind] of PARAM_FIELDS[r.type] || []) {
          const v = r[k];
          if (v === undefined || v === '') throw new Error(`${r.type}: ${k} is required`);
          out[k] = kind === 'json' ? JSON.parse(v) : kind === 'number' ? Number(v) : v;
        }
        return out;
      });
      if (!name.trim() || !input.trim()) throw new Error('Give the case a name and a prompt');
      if (!graders.length) throw new Error('Add at least one grader');
      const expectation = structured ? { criteria: graders.map((g, i) => ({ id: 'criterion-' + (i + 1), description: rows[i].description || (graderTypes[g.type]?.label || g.type), type: g.type, grader: g })) } : null;
      if (structured && !approved) throw new Error('Review and approve the expectation criteria before saving');
      onSave({ name, input, setup: fixture ? [{ title: '{{task}}', description: 'Eval fixture', priority: 'Medium' }] : [], graders, tags: kase?.tags || [], ...(structured ? { expectation, approveExpectation: approved } : {}) });
    } catch (e) { setErr(e instanceof SyntaxError ? 'A JSON field is not valid JSON' : e.message); }
  };
  const field = { width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.ink, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
  const label = { fontSize: 12, fontWeight: 600, color: T.ink2, margin: '12px 0 5px', display: 'block' };
  return (
    <div role="dialog" aria-modal="true" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(8,12,24,.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ width: 600, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>{kase?.caseId ? 'Edit eval case' : 'New eval case'}</div>
          <button onClick={onClose} aria-label="Close" style={iconBtn}><X size={16} /></button>
        </div>
        <div style={{ padding: 12, marginTop: 12, border: `1px solid ${T.border}`, borderRadius: 9, background: T.surface2 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={structured} onChange={e => { setStructured(e.target.checked); setApproved(false); }} />
            Versioned expectations (recommended)
          </label>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 6 }}>Criteria are derived from the graders below. Review them before approving. Changes create a new expectation version.</div>
          {structured && <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginTop: 10 }}>
            <input type="checkbox" checked={approved} onChange={e => setApproved(e.target.checked)} />
            I reviewed and approve these success criteria
          </label>}
        </div>
        <label style={label}>Name</label>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Reminder updates the due date" style={field} />
        <label style={label}>Prompt sent to the agent <span style={{ fontWeight: 400, color: T.ink4 }}>(use {'{{task}}'} for a unique task title)</span></label>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={3} placeholder={'Set a reminder for "{{task}}" tomorrow at 9:00 AM.'} style={{ ...field, resize: 'vertical' }} />
        <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
          <input type="checkbox" checked={fixture} onChange={(e) => setFixture(e.target.checked)} /> Create a task called {'{{task}}'} before the run (deleted afterwards)
        </label>
        <label style={label}>Graders <span style={{ fontWeight: 400, color: T.ink4 }}>(all must pass; prefer checks on tools and data over the reply text)</span></label>
        {rows.map((r, i) => (
          <div key={i} style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 10, marginBottom: 8, background: T.surface2 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <select value={r.type} onChange={(e) => setRows((rs) => rs.map((x, j) => (j === i ? { type: e.target.value } : x)))} style={{ ...field, flex: 1 }}>
                {Object.entries(graderTypes).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove grader" style={{ ...iconBtn, color: T.red }}><Trash2 size={15} /></button>
            </div>
            {(PARAM_FIELDS[r.type] || []).map(([k, lab, ph, kind]) => (
              <div key={k} style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11.5, color: T.ink3, marginBottom: 3 }}>{lab}</div>
                {kind === 'area'
                  ? <textarea value={r[k] || ''} onChange={(e) => setRow(i, { [k]: e.target.value })} rows={2} placeholder={ph} style={{ ...field, resize: 'vertical' }} />
                  : <input value={r[k] ?? ''} onChange={(e) => setRow(i, { [k]: e.target.value })} placeholder={ph} type={kind === 'number' ? 'number' : 'text'} style={field} />}
              </div>
            ))}
          </div>
        ))}
        <button onClick={() => setRows((rs) => [...rs, { type: 'tool_called' }])} style={{ ...btnGhost, marginTop: 4 }}><Plus size={13} /> Add grader</button>
        {err && <div style={{ color: T.red, fontSize: 12.5, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>{saving ? 'Saving…' : 'Save case'}</button>
        </div>
      </div>
    </div>
  );
}
const btnGhost = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' };
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 9, border: 'none', background: T.accent, color: 'var(--app-on-accent)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' };

// ── Panel ────────────────────────────────────────────────────────────────────
export default function EvalsPanel({ isMobile }) {
  const [suites, setSuites] = useState([]);
  const [graderTypes, setGraderTypes] = useState({});
  const [suiteId, setSuiteId] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runId, setRunId] = useState(null);
  const [run, setRun] = useState(null);
  const [trials, setTrials] = useState(3);
  const [starting, setStarting] = useState(false);
  const [openCase, setOpenCase] = useState(null);
  const [editor, setEditor] = useState(null);     // { suiteId, kase }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const poll = useRef(null);

  const loadSuites = useCallback(async (keep) => {
    const d = await evalsAPI.suites();
    setSuites(d.suites || []); setGraderTypes(d.graderTypes || {});
    setSuiteId((cur) => keep && cur ? cur : (d.suites?.[0]?._id || null));
  }, []);
  useEffect(() => { loadSuites(false).catch(() => toast.error('Could not load evals')).finally(() => setLoading(false)); }, [loadSuites]);

  const loadRuns = useCallback(async (selectLatest) => {
    if (!suiteId) return;
    const d = await evalsAPI.listRuns({ suiteId, limit: 20 });
    setRuns(d.runs || []);
    if (selectLatest) setRunId(d.runs?.[0]?._id || null);
  }, [suiteId]);
  useEffect(() => { setRun(null); setRunId(null); loadRuns(true).catch(() => {}); }, [suiteId, loadRuns]);

  // Load the selected run; while it is running, poll until it finishes.
  useEffect(() => {
    clearTimeout(poll.current);
    if (!runId) { setRun(null); return undefined; }
    let stop = false;
    const tick = async () => {
      try {
        const d = await evalsAPI.getRun(runId);
        if (stop) return;
        setRun(d.run);
        if (d.run.status === 'running') poll.current = setTimeout(tick, 2000);
        else loadRuns(false).catch(() => {});
      } catch (_) { /* leave the last state */ }
    };
    tick();
    return () => { stop = true; clearTimeout(poll.current); };
  }, [runId, loadRuns]);

  const suite = suites.find((s) => s._id === suiteId);
  const builtin = !!suite?.builtin;
  const running = run?.status === 'running';

  const start = async () => {
    setStarting(true);
    try {
      const d = await evalsAPI.startRun({ suiteId, trials });
      await loadRuns(false);
      setRunId(d.runId);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not start the evaluation'); }
    finally { setStarting(false); }
  };
  const cancel = async () => { try { await evalsAPI.cancelRun(runId); toast('Stopping after the current trials…'); } catch (_) { /* ignore */ } };

  const saveCase = async (payload) => {
    setSaving(true);
    try {
      if (editor.kase?.caseId) await evalsAPI.updateCase(editor.suiteId, editor.kase.caseId, payload);
      else await evalsAPI.addCase(editor.suiteId, payload);
      toast.success('Case saved');
      const wasMine = editor.suiteId === 'mine';
      setEditor(null);
      const d = await evalsAPI.suites();
      setSuites(d.suites || []);
      if (wasMine) setSuiteId(d.suites.find((x) => x.name === 'My evals')?._id || suiteId);
    } catch (e) { toast.error(e?.response?.data?.error || 'Could not save the case'); }
    finally { setSaving(false); }
  };
  const copyToMine = async (kase) => {
    try { await evalsAPI.addCase('mine', { name: kase.name, input: kase.input, setup: kase.setup, graders: kase.graders, tags: kase.tags }); toast.success('Copied to My evals'); await loadSuites(true); }
    catch (e) { toast.error(e?.response?.data?.error || 'Could not copy'); }
  };
  const deleteCase = async (kase) => {
    if (!window.confirm(`Delete "${kase.name}"?`)) return;
    try { await evalsAPI.deleteCase(suiteId, kase.caseId); setOpenCase(null); await loadSuites(true); } catch (_) { toast.error('Could not delete'); }
  };

  const results = run?.results || [];
  const byCase = useMemo(() => {
    const m = new Map();
    results.forEach((r) => { if (!m.has(r.caseId)) m.set(r.caseId, []); m.get(r.caseId).push(r); });
    return m;
  }, [results]);
  const summary = run?.summary;
  const baseline = useMemo(() => {
    if (!run) return null;
    const i = runs.findIndex((r) => r._id === run._id);
    return runs.slice(i + 1).find((r) => r.status === 'done' && r.summary) || null;
  }, [run, runs]);
  const prev = baseline?.summary;
  const prevCase = (id) => prev?.perCase?.find((c) => c.caseId === id);
  const live = useMemo(() => (summary ? null : (() => {   // rough numbers while a run is in progress
    const ok = results.filter((r) => r.status === 'pass').length;
    return { passRate: results.length ? ok / results.length : null };
  })()), [summary, results]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: T.ink4 }}><Loader2 size={20} style={{ animation: 'mcSpin 1s linear infinite' }} /></div>;

  const cases = suite?.cases || [];
  const caseObj = openCase ? cases.find((c) => c.caseId === openCase) : null;
  const cardGrid = { display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0,1fr))' : 'repeat(5, minmax(0,1fr))', gap: 12, marginBottom: 18 };
  const cols = isMobile ? '1fr 90px' : 'minmax(0,1.6fr) 120px 110px 100px 100px 120px 28px';

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={suiteId || ''} onChange={(e) => setSuiteId(e.target.value)} style={{ height: 36, padding: '0 10px', borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface, color: T.ink, fontSize: 13, fontFamily: 'inherit', maxWidth: isMobile ? '100%' : 340 }}>
          {suites.map((s) => <option key={s._id} value={s._id}>{s.name}{s.builtin ? ' (built-in)' : ''}</option>)}
        </select>
        <label style={{ fontSize: 13, color: T.ink2, display: 'flex', alignItems: 'center', gap: 6 }} title="Each case runs this many times. Models are non-deterministic, so one pass proves little.">
          Trials per case
          <select value={trials} onChange={(e) => setTrials(Number(e.target.value))} style={{ height: 36, padding: '0 8px', borderRadius: 9, border: `1px solid ${T.border}`, background: T.surface, color: T.ink, fontSize: 13, fontFamily: 'inherit' }}>
            {[1, 3, 5].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        {running
          ? <button onClick={cancel} style={{ ...btnGhost, color: T.red, borderColor: T.redBd, background: T.redBg }}><Square size={12} /> Stop</button>
          : <button onClick={start} disabled={starting || !cases.length} style={{ ...btnPrimary, opacity: starting || !cases.length ? 0.6 : 1 }}>{starting ? <Loader2 size={14} style={{ animation: 'mcSpin 1s linear infinite' }} /> : <Play size={13} />} Run evaluation</button>}
        <button onClick={() => setEditor({ suiteId: builtin ? 'mine' : suiteId, kase: null })} style={btnGhost}><Plus size={13} /> New case</button>
        <span style={{ fontSize: 12, color: T.ink4, marginLeft: 'auto' }}>Runs the real agent on your account; fixtures are deleted after each trial.</span>
      </div>

      {suite?.description && <div style={{ fontSize: 13, color: T.ink3, marginBottom: 14 }}>{suite.description}</div>}

      {/* Progress */}
      {running && (
        <div style={{ background: T.accentLight, border: `1px solid ${T.accentMid}`, borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: T.accentDark, fontWeight: 600 }}>
            <span><Loader2 size={13} style={{ animation: 'mcSpin 1s linear infinite', verticalAlign: -2 }} /> Running {run.done} of {run.total} trials</span>
            <span>{live?.passRate != null ? `${pct(live.passRate)} passing so far` : ''}</span>
          </div>
          <div style={{ height: 6, background: T.surface, borderRadius: 99, marginTop: 8, overflow: 'hidden' }}><div style={{ width: `${(run.done / Math.max(1, run.total)) * 100}%`, height: '100%', background: T.accent, transition: 'width .4s' }} /></div>
        </div>
      )}
      {run && (run.status === 'error' || run.status === 'cancelled') && (
        <div style={{ background: T.amberBg, border: `1px solid ${T.amberBd}`, color: T.amber, borderRadius: 12, padding: '10px 16px', marginBottom: 16, fontSize: 13 }}>
          {run.status === 'cancelled' ? 'This run was stopped early; numbers cover the trials that finished.' : `This run failed: ${run.error || 'unknown error'}`}
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div style={cardGrid}>
          <Metric label="Pass rate" value={pct(summary.passRate)} color={tone(summary.passRate).fg} sub={<Delta now={summary.passRate} before={prev?.passRate} />} hint="Share of all trials where every grader passed" />
          <Metric label="Reliability" value={pct(summary.reliability)} color={tone(summary.reliability).fg} sub="cases that passed every trial" hint="pass^k: the share of cases that passed ALL trials. This is what users feel." />
          <Metric label="Capability" value={pct(summary.capability)} sub="cases that passed at least once" hint="pass@k: the share of cases that passed at least once. The gap to Reliability is flakiness." />
          <Metric label="Latency" value={secs(summary.p50Ms)} sub={`p95 ${secs(summary.p95Ms)}`} />
          <Metric label="Tokens / trial" value={summary.avgTokens ?? '—'} sub={`${summary.avgLlmCalls ?? '—'} model calls · ${summary.avgToolCalls ?? '—'} tool calls avg`} />
        </div>
      )}

      {/* History */}
      {runs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 18, overflowX: 'auto', paddingBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: T.ink4, alignSelf: 'center', marginRight: 4 }}>History</span>
          {[...runs].reverse().map((r) => {
            const rate = r.summary?.passRate;
            const t = rate != null ? tone(rate) : null;
            return (
              <button key={r._id} onClick={() => setRunId(r._id)} title={`${new Date(r.createdAt).toLocaleString()} · ${r.trials} trial(s) · ${r.status}`}
                style={{ minWidth: 64, padding: '6px 8px', borderRadius: 9, border: `1px solid ${r._id === runId ? T.accent : T.border}`, background: r._id === runId ? T.accentLight : T.surface, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t ? t.fg : T.ink3 }}>{r.status === 'running' ? '…' : pct(rate)}</div>
                <div style={{ fontSize: 10.5, color: T.ink4 }}>{new Date(r.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} {new Date(r.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</div>
              </button>
            );
          })}
        </div>
      )}

      {/* Cases */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {!isMobile && (
          <div style={{ display: 'grid', gridTemplateColumns: cols, padding: '10px 20px', borderBottom: `1px solid ${T.border}`, background: T.surface2 }}>
            {['CASE', 'TRIALS', 'STATUS', 'LATENCY', 'TOKENS', 'VS PREVIOUS', ''].map((h) => <div key={h} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', color: T.ink4 }}>{h}</div>)}
          </div>
        )}
        {cases.length === 0 && <div style={{ padding: '36px 20px', textAlign: 'center', color: T.ink3, fontSize: 13 }}><FlaskConical size={22} style={{ display: 'block', margin: '0 auto 8px', color: T.ink4 }} />No cases yet. Add one, or open a run in the Runs tab and choose "Add to evals".</div>}
        {cases.map((c) => {
          const rs = (byCase.get(c.caseId) || []).sort((a, b) => a.trial - b.trial);
          const s = summary?.perCase?.find((x) => x.caseId === c.caseId);
          const passes = rs.filter((r) => r.status === 'pass').length;
          const status = !rs.length ? 'pending' : s ? s.status : passes === rs.length ? 'passing' : passes === 0 ? 'failing' : 'flaky';
          const st = STATUS_STYLE[status];
          const p = prevCase(c.caseId);
          const rate = rs.length ? passes / rs.length : null;
          const change = p && rate != null ? Math.round((rate - p.passRate) * 100) : null;
          const lat = s?.p50Ms ?? (rs.length ? rs[rs.length - 1].latencyMs : null);
          return (
            <div key={c.caseId} onClick={() => setOpenCase(c.caseId)} style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'center', gap: 8, padding: '12px 20px', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = T.surface2; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
                <div style={{ fontSize: 11.5, color: T.ink4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(c.tags || []).join(' · ')}{c.source?.rating === 'down' ? ' · reported by a user' : ''}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><TrialDots results={rs} trials={run?.trials || trials} />{!isMobile && rs.length > 0 && <span style={{ fontSize: 12, color: T.ink3 }}>{passes}/{rs.length}</span>}</div>
              {!isMobile && <div><Chip s={st}>{st.label}</Chip></div>}
              {!isMobile && <div style={{ fontSize: 13, color: T.ink2 }}>{secs(lat)}</div>}
              {!isMobile && <div style={{ fontSize: 13, color: T.ink2 }}>{s?.avgTokens ?? '—'}</div>}
              {!isMobile && <div style={{ fontSize: 12.5, fontWeight: 600, color: change == null ? T.ink4 : change < 0 ? T.red : change > 0 ? T.green : T.ink4 }}>{change == null ? '—' : change === 0 ? 'same' : `${change > 0 ? '▲' : '▼'} ${Math.abs(change)} pts`}</div>}
              {!isMobile && <ChevronRight size={15} color="var(--app-text-3)" />}
              {isMobile && <div><Chip s={st}>{st.label}</Chip></div>}
            </div>
          );
        })}
      </div>

      {caseObj && (
        <CaseDrawer
          kase={caseObj} results={(byCase.get(caseObj.caseId) || []).sort((a, b) => a.trial - b.trial)} trials={run?.trials || 1} builtin={builtin}
          onClose={() => setOpenCase(null)}
          onEdit={() => { setEditor({ suiteId, kase: caseObj }); setOpenCase(null); }}
          onCopy={() => copyToMine(caseObj)} onDelete={() => deleteCase(caseObj)}
        />
      )}
      {editor && <CaseEditor kase={editor.kase} graderTypes={graderTypes} saving={saving} onClose={() => setEditor(null)} onSave={saveCase} />}
    </div>
  );
}
